import React, { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  Upload,
  Button,
  Alert,
  Space,
  message,
  Typography,
  Divider,
} from 'antd';
import { UploadOutlined, DollarOutlined, FileTextOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import dayjs from 'dayjs';
import {
  EXPENSE_TYPE_OPTIONS,
  PAYMENT_NATURE_OPTIONS,
  FILE_UPLOAD_CONFIG,
} from '../../constants/financeConstants';
import {
  createFinanceRecord,
  uploadAttachment,
  EntityType,
  type CreateFinanceRecordRequest,
} from '../../services/finance';

const { TextArea } = Input;
const { Text } = Typography;

interface AddFinanceRecordModalProps {
  visible: boolean;
  leadId: number;
  onCancel: () => void;
  onSuccess: () => void;
}

const AddFinanceRecordModal: React.FC<AddFinanceRecordModalProps> = ({
  visible,
  leadId,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      // Prepare finance record data
      const recordData: Omit<CreateFinanceRecordRequest, 'entity_type' | 'entity_id'> = {
        expense_type: values.expense_type,
        payment_nature: values.payment_nature,
        amount: values.amount,
        payment_time: values.payment_time
          ? dayjs(values.payment_time).format('YYYY-MM-DDTHH:mm:ss')
          : undefined,
        remark: values.remark,
      };

      // Step 1: Create finance record
      const createdRecord = await createFinanceRecord(EntityType.LEAD, leadId, recordData);

      // Step 2: Upload attachments if any
      if (fileList.length > 0) {
        const uploadPromises = fileList.map((file) => {
          if (file.originFileObj) {
            return uploadAttachment(EntityType.LEAD, leadId, createdRecord.id, file.originFileObj);
          }
          return Promise.resolve();
        });

        await Promise.all(uploadPromises);
      }

      message.success('财务记录创建成功');
      form.resetFields();
      setFileList([]);
      onCancel();
      // Call onSuccess after closing modal to ensure refresh happens
      onSuccess();
    } catch (error: any) {
      console.error('Failed to create finance record:', error);
      message.error(error.message || '创建财务记录失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    onCancel();
  };

  const uploadProps: UploadProps = {
    fileList,
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      // Validate file size
      if (file.size > FILE_UPLOAD_CONFIG.maxSize) {
        message.error(`文件 ${file.name} 大小超过20MB限制`);
        return Upload.LIST_IGNORE;
      }

      // Validate file type
      if (!FILE_UPLOAD_CONFIG.acceptedTypes.includes(file.type)) {
        message.error(`文件 ${file.name} 格式不支持，仅支持：${FILE_UPLOAD_CONFIG.accept}`);
        return Upload.LIST_IGNORE;
      }

      // Prevent auto upload
      return false;
    },
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    maxCount: FILE_UPLOAD_CONFIG.maxCount,
    accept: FILE_UPLOAD_CONFIG.accept,
    multiple: true,
  };

  return (
    <Modal
      title={
        <Space>
          <DollarOutlined style={{ color: '#1890ff' }} />
          <span style={{ fontSize: '16px', fontWeight: 600 }}>新增账单</span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={600}
      okText="创建账单"
      cancelText="取消"
      styles={{ body: { paddingTop: 24 } }}
    >
      <Alert
        message="重要提示"
        description="财务记录创建后不可修改，请仔细核对所有信息"
        type="warning"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark="optional"
      >
        {/* 费用信息 */}
        <Divider orientation="left" style={{ marginTop: 0 }}>
          <Text type="secondary" style={{ fontSize: '13px' }}>费用信息</Text>
        </Divider>

        <Form.Item
          name="expense_type"
          label="费用类型"
          rules={[{ required: true, message: '请选择费用类型' }]}
        >
          <Select
            placeholder="请选择费用类型"
            size="large"
            options={EXPENSE_TYPE_OPTIONS}
          />
        </Form.Item>

        <Form.Item
          name="payment_nature"
          label="款项性质"
          rules={[{ required: true, message: '请选择款项性质' }]}
        >
          <Select
            placeholder="请选择款项性质"
            size="large"
            options={PAYMENT_NATURE_OPTIONS}
          />
        </Form.Item>

        {/* 金额和时间 */}
        <Divider orientation="left">
          <Text type="secondary" style={{ fontSize: '13px' }}>金额和时间</Text>
        </Divider>

        <Form.Item
          name="amount"
          label="动款金额（元）"
          rules={[
            { required: true, message: '请输入动款金额' },
            {
              type: 'number',
              min: 0.01,
              message: '金额必须大于0',
            },
          ]}
        >
          <InputNumber
            prefix="¥"
            style={{ width: '100%' }}
            size="large"
            precision={2}
            min={0.01}
            step={100}
            placeholder="请输入金额"
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            }
            parser={(value) => value!.replace(/\$\s?|(,*)/g, '') as any}
          />
        </Form.Item>

        <Form.Item name="payment_time" label="动款时间">
          <DatePicker
            showTime
            style={{ width: '100%' }}
            size="large"
            placeholder="选择动款时间（可选）"
            format="YYYY-MM-DD HH:mm"
          />
        </Form.Item>

        {/* 备注和附件 */}
        <Divider orientation="left">
          <Text type="secondary" style={{ fontSize: '13px' }}>备注和附件</Text>
        </Divider>

        <Form.Item name="remark" label="备注">
          <TextArea
            rows={3}
            placeholder="可说明支付方式、收款账户等信息"
            showCount
            maxLength={500}
          />
        </Form.Item>

        <Form.Item label="附件">
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />} size="large">
              选择文件
            </Button>
          </Upload>
          <div style={{ color: '#999', fontSize: 12, marginTop: 8 }}>
            支持PDF、Word、Excel、图片格式，单个文件不超过20MB，最多上传5个文件
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddFinanceRecordModal;
